/**
 * Shared system-status model for the /ops System Health page AND the header
 * status dot, so the two can never disagree: both subscribe to the ONE
 * module-level store below (same fetches, same derivation, same rollup).
 *
 * Severity ladder (operator's graduated scheme):
 *   green  — everything up
 *   yellow — minor / informational (data stale within tolerance, unacked
 *            warnings, coverage gaps, audit chain not yet verified)
 *   orange — automation impaired but the app still serves (scheduler hung,
 *            circuit tripped, migrations degraded, ETL critically stale,
 *            BQ unreachable, ops sync errors)
 *   red    — truly down / critical (DB not queryable, backend unreachable,
 *            an unregistered job running outside the ownership map)
 *   unknown— not fetched yet / couldn't determine (ranks low so it never
 *            false-alarms, but is NOT treated as green)
 *
 * Every subsystem severity is grounded in a REAL backend signal — no invented
 * thresholds beyond the ones the existing tooling already uses
 * (scripts/dev/prod-status.ps1: sync source stale after 26h; readiness:
 * scheduler heartbeat stale after 180s).
 */
import { useSyncExternalStore } from 'react'
import { ageHoursSince, fmtMonthDay } from './format/date'
import {
  api, apiObs, apiOps,
  type HealthOut, type ReadinessOut, type EngineState, type SchedulerJob,
  type Run, type CircuitState, type FreshnessOverviewResponse,
  type FreshnessJob, type FreshnessAlert, type AlertRow, type AlertSummaryOut,
  type OpsDeepHealth, type SyncJobsOut, type FreshnessUnifiedOut,
} from './api'
import { tryStatusRollup } from './statusRollup'

export type Sev = 'green' | 'yellow' | 'orange' | 'red' | 'unknown'

export const SEV_RANK: Record<Sev, number> = {
  green: 0, unknown: 1, yellow: 2, orange: 3, red: 4,
}

export function worstSev(sevs: Sev[]): Sev {
  return sevs.reduce<Sev>((a, b) => (SEV_RANK[b] > SEV_RANK[a] ? b : a), 'green')
}

/**
 * Every string literal any derive body currently assigns to Issue.key,
 * enumerated from this file's derive bodies (deriveDatabase through
 * deriveRegistry, :133-628). A key rename or typo in any derive body is now
 * a compile error here — see opsStatusIssueKeyFixture.ts for the pinned
 * negative case (a key outside this union fails `tsc`).
 *
 * D-signal-taxonomy: 'freshness-stale' is DROPPED — deriveFreshness's
 * rewritten body now emits 'freshness-stale-local' / 'freshness-stale-
 * upstream' (a category split A pre-provisioned this union for) instead of
 * one undifferentiated key, and 'freshness-review-due' is NEW (an overdue
 * lifecycle mute — migration 147's anti-mute-forever resurfacing line).
 *
 * `sync-section-${string}` is a template-literal member, not a typo: the one
 * dynamic key in deriveSyncState appends a truncated error string
 * (`sync-section-${e.slice(0, 12)}`) to disambiguate which unified-freshness
 * section failed to read, so no single string literal can cover it.
 */
export type IssueKey =
  | 'db-unreachable'
  | 'db-down'
  | 'migrations-degraded'
  | 'migrations-ops-manifest'
  | 'sched-down'
  | 'sched-stale'
  | 'sched-noheartbeat'
  | 'engine-lasterror'
  | 'circuit-tripped'
  | 'freshness-stale-local'
  | 'freshness-stale-upstream'
  | 'freshness-review-due'
  | 'alerts-critical'
  | 'alerts-warning'
  | 'opshealth-bq'
  | 'opshealth-audit'
  | 'opshealth-coverage'
  | 'opshealth-tick'
  | 'opshealth-infra'
  | 'opshealth-tripwire'
  | 'sync-error'
  | 'sync-degraded'
  | 'sync-stale'
  | `sync-section-${string}`
  | 'registry-unknown-jobs'
  | 'registry-db-split'

/** A non-green condition the operator should see, with the offending values. */
export type Issue = {
  key: IssueKey
  sev: Exclude<Sev, 'green' | 'unknown'>
  subsystem: string
  title: string
  detail: string
  hint?: string
  /**
   * Structured evidence a fix action or an InfoItem promotion can read
   * without parsing `detail`'s free text. Optional and additive — every
   * existing `issues.push({...})` call compiles unchanged without it; D
   * attaches it when rewriting a body, B reads it to build fix actions.
   */
  meta?: {
    category?: string
    lastSuccess?: string | null
    lastDataDate?: string | null
    runsWhere?: string
    sources?: string[]
    count?: number
    /** PR 8: `count` is of open problems (incidents), not of raw alert rows. */
    incidents?: boolean
  }
}

/**
 * An Issue is counted, colors the page, and must be genuinely actionable by
 * a human. An InfoItem is always VISIBLE but never counted and never
 * affects sev. Moving a condition from Issue to InfoItem requires that its
 * deviation-signal survives elsewhere (a tripwire, an expiry,
 * auto-promotion) — suppression is forbidden.
 */
export type InfoItem = {
  key: string
  subsystem: string
  tone: 'guardrail' | 'waiting' | 'decision' | 'quality' | 'historical'
  title: string
  detail?: string
  meta?: Record<string, unknown>
}

export type Subsystem = {
  id: string
  name: string
  sev: Sev
  summary: string
  issues: Issue[]
  info: InfoItem[]
}

/** Result of a settled fetch — never throws, so one failure can't blank others. */
export type Src<T> = { data: T | null; error: string | null }

export async function settle<T>(p: Promise<T>): Promise<Src<T>> {
  try {
    return { data: await p, error: null }
  } catch (e) {
    return { data: null, error: e instanceof Error ? e.message : String(e) }
  }
}

export type OpsSources = {
  health: Src<HealthOut>
  readiness: Src<ReadinessOut>
  engine: Src<EngineState>
  jobs: Src<SchedulerJob[]>
  runs: Src<Run[]>
  circuit: Src<CircuitState>
  freshness: Src<FreshnessOverviewResponse>
  freshnessAlerts: Src<FreshnessAlert[]>
  alerts: Src<AlertRow[]>
  // Exact unacked counts (GROUP BY over the whole bbpro_alerts table) — the
  // undercount fix. `alerts` above stays a capped 50-row list (used only for
  // detail-string previews); this is what decides severity/counts so a
  // subsystem can never read GREEN over hundreds of unacked errors just
  // because they're older than the newest 50 (447 unacked observed in prod).
  alertSummary: Src<AlertSummaryOut>
  // Ops half — first-class subsystems (product review #2).
  opsDeep: Src<OpsDeepHealth>
  syncJobs: Src<SyncJobsOut>
  unified: Src<FreshnessUnifiedOut>
}

/**
 * Fetch every source once.
 *
 * Build 51a: prefer the ONE-request rollup; fall back to the thirteen-way
 * fan-out below when the backend doesn't serve it. The two produce the same
 * OpsSources value from the same handlers, so everything downstream — the
 * dot, the bell, SystemHealth — is untouched either way.
 */
export async function fetchOpsSources(fresh = false): Promise<OpsSources> {
  const rolled = await tryStatusRollup(fresh)
  if (rolled) return rolled
  // The fan-out has no server-side cache to bypass — every call already
  // hits the live handlers, so `fresh` has nothing to do here.
  return fetchOpsSourcesFanout()
}

/** The original thirteen-request fan-out, kept as the fallback path. */
export async function fetchOpsSourcesFanout(): Promise<OpsSources> {
  const [
    health, readiness, engine, jobs, runs, circuit,
    freshness, freshnessAlerts, alerts, alertSummary, opsDeep, syncJobs, unified,
  ] = await Promise.all([
    settle(apiObs.getHealth()), settle(apiObs.getReadiness()), settle(apiObs.getEngineState()),
    settle(api.schedulerJobs()), settle(api.listRuns(5)), settle(api.getCircuitBreaker()),
    settle(api.getFreshness()), settle(api.getFreshnessAlerts().then((r) => r.alerts)),
    settle(apiObs.listAlerts({ limit: 50 })),
    // Same Promise.all, same 30s timer as everything else here — the bell
    // adds ZERO new polling (clickpay-polling-request-storm: tabs left open
    // ~2h caused unbounded polling and Chrome ERR_INSUFFICIENT_RESOURCES).
    settle(apiObs.getAlertSummary()),
    settle(apiOps.getDeepHealth()), settle(apiOps.getSyncJobs()),
    settle(apiOps.getFreshnessUnified()),
  ])
  return {
    health, readiness, engine, jobs, runs, circuit,
    freshness, freshnessAlerts, alerts, alertSummary, opsDeep, syncJobs, unified,
  }
}

// ── Per-subsystem derivation ────────────────────────────────────────────────

function deriveDatabase(s: OpsSources): Subsystem {
  const r = s.readiness
  const issues: Issue[] = []
  let sev: Sev = 'unknown'
  let summary = 'unknown'
  if (r.error) {
    // getReadiness only throws on a non-200/503 response or a network failure,
    // i.e. the backend is unreachable — treat as down.
    sev = 'red'
    summary = 'backend unreachable'
    issues.push({
      key: 'db-unreachable', sev: 'red', subsystem: 'Database',
      title: 'Backend unreachable',
      detail: `/api/readiness did not respond: ${r.error}`,
      hint: 'If multiple cards failed together, the DB may be invalidated — see the SSH recovery runbook.',
    })
  } else if (r.data) {
    const db = r.data.checks.db
    sev = db ? 'green' : 'red'
    summary = db ? 'queryable' : 'NOT queryable'
    if (!db) {
      const dbErr = r.data.errors.find((e) => e.startsWith('db:')) ?? 'SELECT 1 failed'
      issues.push({
        key: 'db-down', sev: 'red', subsystem: 'Database',
        title: 'Database not queryable',
        detail: dbErr,
        hint: 'Classic DuckDB index-invalidation signature — automation is frozen. Recover via the SSH runbook (process must be DOWN).',
      })
    }
  }
  return { id: 'database', name: 'Database', sev, summary, issues, info: [] }
}

function deriveMigrations(s: OpsSources): Subsystem {
  const r = s.readiness
  const issues: Issue[] = []
  let sev: Sev = 'unknown'
  let summary = 'unknown'
  if (r.data) {
    const ok = r.data.checks.migrations_ok
    sev = ok ? 'green' : 'orange'
    summary = ok ? 'ok' : 'DEGRADED'
    if (!ok) {
      const mErr = r.data.errors.find((e) => e.startsWith('migrations')) ?? 'a migration failed'
      issues.push({
        key: 'migrations-degraded', sev: 'orange', subsystem: 'Migrations',
        title: 'Migrations degraded (schema frozen at last good migration)',
        detail: mErr,
        hint: 'Fix the migration file and restart the backend.',
      })
    }
  }
  // The ops half re-hashes its migrations MANIFEST on demand — a mismatch
  // means an edited/missing migration file (drift from what was applied).
  if (s.opsDeep.data && s.opsDeep.data.migrations_manifest_ok === false) {
    sev = worstSev([sev, 'orange'])
    summary += ' · ops manifest BAD'
    issues.push({
      key: 'migrations-ops-manifest', sev: 'orange', subsystem: 'Migrations',
      title: 'Ops migrations manifest mismatch',
      detail: 'migrations/ops/MANIFEST.txt no longer matches the migration files on disk.',
      hint: 'A migration file was edited or removed after being applied — restore it or re-hash deliberately.',
    })
  }
  return { id: 'migrations', name: 'Migrations & Schema', sev, summary, issues, info: [] }
}

function deriveScheduler(s: OpsSources): Subsystem {
  const r = s.readiness
  const issues: Issue[] = []
  let sev: Sev = 'unknown'
  let summary = 'unknown'
  if (r.data) {
    const c = r.data.checks
    const age = c.scheduler_heartbeat_age_s
    if (!c.scheduler_running_flag) {
      sev = 'orange'
      summary = 'NOT running'
      issues.push({
        key: 'sched-down', sev: 'orange', subsystem: 'Scheduler',
        title: 'Scheduler not running',
        detail: 'APScheduler .running flag is false — no rules or syncs are firing.',
      })
    } else if (age != null && age > 180) {
      sev = 'orange'
      summary = `heartbeat stale ${age.toFixed(0)}s`
      issues.push({
        key: 'sched-stale', sev: 'orange', subsystem: 'Scheduler',
        title: 'Scheduler heartbeat stale',
        detail: `running flag is true but no heartbeat in ${age.toFixed(0)}s (>180s) — thread likely dead/hung, jobs aren't firing.`,
      })
    } else if (age == null) {
      sev = 'yellow'
      summary = 'no heartbeat yet'
      issues.push({
        key: 'sched-noheartbeat', sev: 'yellow', subsystem: 'Scheduler',
        title: 'Scheduler heartbeat missing',
        detail: 'No bbpro_app_heartbeat row yet (brief at boot; persistent = a problem).',
      })
    } else {
      sev = 'green'
      summary = `live · beat ${age.toFixed(0)}s ago`
    }
  }
  // Engine last-error is a softer signal layered on top (never downgrades red).
  // D-signal-taxonomy: classify off the NEWEST run (eng.last_run), not the
  // compat-only eng.last_error, which the 08-14 fossil proved can point at
  // an old cap_hit/needs_confirmation refusal instead of today's real
  // failure. An old-shaped backend (no last_run) degrades to exactly
  // today's eng.last_error behavior — never a false green.
  const eng = s.engine.data
  const info: InfoItem[] = []
  const lastRun = eng?.last_run
  if (lastRun) {
    if (lastRun.classification === 'error' && sev === 'green') {
      sev = 'yellow'
      summary += ' · last fire errored'
      const top = lastRun.top_record_error
      const msg = (top?.message ?? lastRun.status ?? 'error').slice(0, 120)
      issues.push({
        key: 'engine-lasterror', sev: 'yellow', subsystem: 'Scheduler',
        title: 'Last rule fire reported an error',
        detail: `${lastRun.rule_name ?? 'rule'}: ${msg}`
          + (top ? ` (${top.count} record${top.count > 1 ? 's' : ''})` : ''),
      })
    } else if (lastRun.classification === 'parked_safety') {
      // Never an Issue — the guardrail refusing to fire is the SAFE state,
      // not a bug. Visible regardless of the scheduler's own severity.
      const n = lastRun.records_matched
      info.push({
        key: 'scheduler-guardrail', subsystem: 'Scheduler', tone: 'guardrail',
        title: n != null
          ? `Safety gate held a ${n.toLocaleString()}-record fire — the guardrail working`
          : 'Safety gate held a fire — the guardrail working',
        detail: `${lastRun.rule_name ?? 'A rule'} was stopped before dispatch (${lastRun.status}).`,
      })
    }
  } else if (eng?.last_error && sev === 'green') {
    sev = 'yellow'
    summary += ' · last fire errored'
    issues.push({
      key: 'engine-lasterror', sev: 'yellow', subsystem: 'Scheduler',
      title: 'Last rule fire reported an error',
      detail: `${eng.last_error.rule_name ?? 'rule'}: ${(eng.last_error.message ?? '').slice(0, 120)}`,
    })
  }
  return { id: 'scheduler', name: 'Scheduler & Engine', sev, summary, issues, info }
}

function deriveCircuit(s: OpsSources): Subsystem {
  const c = s.circuit
  const issues: Issue[] = []
  let sev: Sev = 'unknown'
  let summary = 'unknown'
  if (c.data) {
    sev = c.data.tripped ? 'orange' : 'green'
    summary = c.data.tripped ? 'TRIPPED' : 'closed'
    if (c.data.tripped) {
      issues.push({
        key: 'circuit-tripped', sev: 'orange', subsystem: 'Circuit Breaker',
        title: 'Circuit breaker tripped — live writes frozen',
        detail: c.data.tripped_reason
          ? `${c.data.tripped_reason}${c.data.tripped_at ? ` (at ${c.data.tripped_at})` : ''}`
          : 'Automation is frozen (a safe state). Investigate the failing rule before resetting.',
        hint: 'Reset re-arms live writes — fix the root cause first.',
      })
    }
  }
  return { id: 'circuit', name: 'Circuit Breaker', sev, summary, issues, info: [] }
}

/**
 * Per-feed text uses whichever axis actually caused the status. migration
 * 117 grades on data_status independently of job_status — a feed can write
 * on schedule (job fine, age_hours low) while the DATA it moved froze
 * months ago (data_age_hours huge). Rendering bare age_hours on that row
 * reads as an impossible "147h stale" when the write itself is fine; using
 * status_reason to pick the axis that's actually failing kills that
 * absurdity and tells the operator the true story: "has data through
 * <date> — N days behind; the pull itself runs on schedule".
 */
function freshnessFeedText(j: FreshnessJob): string {
  if (j.status_reason === 'data' && j.last_data_date) {
    const days = j.data_age_hours != null ? Math.floor(j.data_age_hours / 24) : null
    return `${j.display_name}: has data through ${j.last_data_date}`
      + (days != null ? ` — ${days}d behind` : '')
      + ' (the pull itself runs on schedule)'
  }
  return `${j.display_name} ${j.age_hours != null ? `${j.age_hours.toFixed(0)}h` : '?'} (${j.status})`
}

function deriveFreshness(s: OpsSources): Subsystem {
  const f = s.freshness
  const issues: Issue[] = []
  const info: InfoItem[] = []
  let sev: Sev = 'unknown'
  let summary = 'unknown'
  if (f.data) {
    const o = f.data.overall
    const counts = o.counts
    const reviewDue = o.review_due ?? 0
    const muted = o.muted
    summary = `${counts.green ?? 0} ok · ${counts.yellow ?? 0} stale · ${(counts.red ?? 0) + (counts.error ?? 0)} critical`
    const mutedTotal = muted ? muted.blocked + muted.planned + muted.retired : 0
    if (mutedTotal > 0) summary += ` · ${mutedTotal} muted`
    switch (o.status) {
      case 'green': sev = 'green'; break
      case 'yellow': sev = 'yellow'; break
      // Critically stale or BQ-unreachable: serious data blindness, but the app
      // still serves → orange (impaired), not red (full down).
      case 'red': sev = 'orange'; break
      case 'error': sev = 'orange'; break
      // No snapshot / BQ never ran happens DURING the incidents this hub exists
      // for — surface as yellow, never silent-green.
      case 'unknown': sev = 'yellow'; break
      default: sev = 'unknown'
    }

    // migration 147 lifecycle (C): a muted feed is a producer-side fact
    // ClickPay cannot fix — never counted (the badge/overall.status above
    // already excludes it server-side) but always individually VISIBLE,
    // with the plain-English reason and its expiry.
    const activeJobs: FreshnessJob[] = []
    for (const j of f.data.jobs) {
      const lifecycle = j.lifecycle ?? 'active'
      if (lifecycle !== 'active') {
        info.push({
          key: `freshness-muted-${j.job_id}`, subsystem: 'Freshness', tone: 'waiting',
          title: j.lifecycle_reason || `${j.display_name} is ${lifecycle}`,
          detail: j.lifecycle_review_by ? `check again by ${j.lifecycle_review_by}` : undefined,
          meta: { job_id: j.job_id, lifecycle },
        })
        continue
      }
      activeJobs.push(j)
    }

    // Anti-mute-forever: an overdue review resurfaces as its OWN counted
    // line — a lifecycle mute is never allowed to go quiet forever.
    if (reviewDue > 0) {
      sev = worstSev([sev, 'yellow'])
      issues.push({
        key: 'freshness-review-due', sev: 'yellow', subsystem: 'Freshness',
        title: `${reviewDue} muted feed${reviewDue > 1 ? 's' : ''} overdue for review`,
        detail: "A lifecycle mute's review-by date has passed — check whether the underlying fact still applies.",
        hint: 'Reactivate, or set a new lifecycle state with a fresh review date.',
      })
    }

    // Split the remaining ACTIVE feeds by category — local_sync (ClickPay's
    // own pull) vs bq_source/adbetter_internal (an upstream ETL problem, not
    // fixable by re-running anything here). One issue per non-empty group.
    const badActive = activeJobs.filter(
      (j) => j.status === 'red' || j.status === 'error' || j.status === 'yellow')
    const localBad = badActive.filter((j) => j.category === 'local_sync')
    const upstreamBad = badActive.filter(
      (j) => j.category === 'bq_source' || j.category === 'adbetter_internal')
    const groups: Array<[IssueKey, FreshnessJob[], string, 'local_sync' | 'bq_source']> = [
      ['freshness-stale-local', localBad, 'local sync', 'local_sync'],
      ['freshness-stale-upstream', upstreamBad, 'upstream', 'bq_source'],
    ]
    for (const [key, group, label, category] of groups) {
      if (!group.length) continue
      const groupSev: Exclude<Sev, 'green' | 'unknown'> =
        group.some((j) => j.status === 'red' || j.status === 'error') ? 'orange' : 'yellow'
      sev = worstSev([sev, groupSev])
      issues.push({
        key, sev: groupSev, subsystem: 'Freshness',
        title: `${group.length} ${label} feed${group.length > 1 ? 's' : ''} stale`,
        detail: group.slice(0, 4).map(freshnessFeedText).join(' · '),
        hint: 'Refresh the stale mirrors, or trigger the upstream ETL.',
        meta: {
          category,
          lastSuccess: group[0]?.last_success_at ?? null,
          runsWhere: group[0]?.runs_where ?? undefined,
          sources: group.map((j) => j.job_id),
        },
      })
    }
  }
  return { id: 'freshness', name: 'Freshness / ETL', sev, summary, issues, info }
}

/**
 * Alerts subsystem — severity/counts here MUST come from the exact
 * summarize_unacknowledged() aggregate (s.alertSummary), never from the
 * capped 50-row list (s.alerts). The list caps at `limit` (default 50); prod
 * has carried 447+ unacked rows, so counting the list alone can silently
 * undercount, or even read GREEN over hundreds of unacked errors if the
 * newest 50 happen to be info-severity. Detail strings (the top-3 preview
 * under each issue) still come from the list — that's just a preview and
 * losing a few old entries off the tail is harmless.
 *
 * Fallback: if /api/alerts/summary itself failed (mixed-version deploy,
 * endpoint down) but the list succeeded, derive counts from the list —
 * the pre-fix behavior — so one endpoint outage can't blank this subsystem
 * entirely.
 *
 * PR 8 (F8): when the summary carries `open_incidents` (migration 288), the
 * severity comes from OPEN INCIDENTS — one per problem, however many rows it
 * wrote, with retired sources (the hourly speed warnings, the listing digest)
 * left out — instead of the raw unacked row count, which double-counted the
 * bell and turned System health amber over 19 copies of an informational
 * warning. Absent (older backend) → the row-count logic below, unchanged.
 */
function deriveAlerts(s: OpsSources): Subsystem {
  const summary = s.alertSummary.data
  const list = s.alerts.data
  if (summary?.open_incidents) return deriveAlertsFromIncidents(summary.open_incidents)
  const issues: Issue[] = []
  let sev: Sev
  let summaryText: string

  let critCount: number
  let warnCount: number
  let totalUnacked: number
  if (summary) {
    critCount = (summary.by_severity.critical ?? 0) + (summary.by_severity.error ?? 0)
    warnCount = summary.by_severity.warning ?? 0
    totalUnacked = summary.total_unacked
  } else if (list) {
    const unacked = list.filter((x) => !x.acknowledged_at)
    const sevOf = (x: AlertRow) => (x.severity ?? '').toLowerCase()
    critCount = unacked.filter((x) => sevOf(x) === 'critical' || sevOf(x) === 'error').length
    warnCount = unacked.filter((x) => sevOf(x) === 'warning').length
    totalUnacked = unacked.length
  } else {
    return { id: 'alerts', name: 'Alerts & Audit', sev: 'unknown', summary: 'unknown', issues, info: [] }
  }

  const unackedList = (list ?? []).filter((x) => !x.acknowledged_at)
  const sevOf = (x: AlertRow) => (x.severity ?? '').toLowerCase()
  const critDetail = unackedList.filter((x) => sevOf(x) === 'critical' || sevOf(x) === 'error')
  const warnDetail = unackedList.filter((x) => sevOf(x) === 'warning')

  if (critCount > 0) {
    sev = 'orange'
    summaryText = `${critCount} unacked critical/error`
    issues.push({
      key: 'alerts-critical', sev: 'orange', subsystem: 'Alerts',
      title: `${critCount} unacknowledged critical alert${critCount > 1 ? 's' : ''}`,
      detail: critDetail.slice(0, 3).map((x) => `${x.source ?? '?'}: ${(x.message ?? '').slice(0, 80)}`).join(' · '),
    })
  } else if (warnCount > 0) {
    sev = 'yellow'
    summaryText = `${warnCount} unacked warning${warnCount > 1 ? 's' : ''}`
    issues.push({
      key: 'alerts-warning', sev: 'yellow', subsystem: 'Alerts',
      title: `${warnCount} unacknowledged warning${warnCount > 1 ? 's' : ''}`,
      detail: warnDetail.slice(0, 3).map((x) => `${x.source ?? '?'}: ${(x.message ?? '').slice(0, 80)}`).join(' · '),
    })
  } else {
    sev = 'green'
    summaryText = totalUnacked ? `${totalUnacked} unacked (info)` : 'none unacked'
  }

  return { id: 'alerts', name: 'Alerts & Audit', sev, summary: summaryText, issues, info: [] }
}

/** PR 8: the Alerts subsystem from open incidents (see deriveAlerts). The
 *  issue detail names what is open by its plain-English title, from the
 *  same `open_incidents` block the count comes from — never from the raw
 *  /api/alerts rows, which also hold rows that closed themselves or whose
 *  producer is retired, so the names and the count could disagree.
 *  `meta.incidents` tells fixPlan the count is of problems, not rows. */
export function deriveAlertsFromIncidents(
  open: NonNullable<AlertSummaryOut['open_incidents']>,
): Subsystem {
  const issues: Issue[] = []
  const crit = (open.by_severity.critical ?? 0) + (open.by_severity.error ?? 0)
  const warn = open.by_severity.warning ?? 0
  const sourcesAt = (sevs: string[]) => (open.by_source ?? [])
    .filter((x) => sevs.includes((x.worst_severity ?? '').toLowerCase()))
    .map((x) => x.title)
    .slice(0, 3)
    .join(' · ')
  const problems = (n: number) => `${n} open system problem${n === 1 ? '' : 's'}`
  let sev: Sev
  let summaryText: string
  if (crit > 0) {
    sev = 'orange'
    summaryText = problems(crit)
    issues.push({ key: 'alerts-critical', sev: 'orange', subsystem: 'Alerts', title: problems(crit),
                  detail: sourcesAt(['critical', 'error']), meta: { count: crit, incidents: true } })
  } else if (warn > 0) {
    sev = 'yellow'
    summaryText = problems(warn)
    issues.push({ key: 'alerts-warning', sev: 'yellow', subsystem: 'Alerts', title: problems(warn),
                  detail: sourcesAt(['warning']), meta: { count: warn, incidents: true } })
  } else {
    sev = 'green'
    summaryText = open.total ? `${open.total} open (info)` : 'no open problems'
  }
  return { id: 'alerts', name: 'Alerts & Audit', sev, summary: summaryText, issues, info: [] }
}

// ── Ops-half subsystems (NEW — product review #2) ───────────────────────────

/** Coverage keys → operator-readable names. */
const COVERAGE_LABEL: Record<string, string> = {
  products_landed_cost_coverage_pct: 'products with landed unit cost',
  products_supplier_coverage_pct: 'products with a resolvable supplier',
  monthly_history_revenue_coverage_pct: 'monthly history revenue coverage',
  orders_stale_eta_count: 'POs with an ETA >30d in the past',
}

/**
 * D-signal-taxonomy: route each warnings_v2 entry by its `kind`. Absent
 * warnings_v2 (old backend) leaves everything below a no-op — the legacy
 * coverage_warnings-driven issue further up is untouched either way, so an
 * old-shaped payload reproduces today's behavior exactly.
 *
 *   completeness      → InfoItem (tone 'quality'), NEVER an Issue.
 *   awaiting_decision → InfoItem (tone 'decision'), NEVER an Issue — the
 *                        legacy dup backlog, dollar figure kept in the title.
 *   infra             → its OWN 'opshealth-infra' Issue, LEAVING the
 *                        generic coverage bucket (own label + runbook hint,
 *                        not just a raw key string).
 *   tripwire          → its OWN 'opshealth-tripwire' Issue (orange, ZERO
 *                        threshold — a real writer regression).
 *   work_queue /
 *   needs_calibration → folded into the SAME generic 'opshealth-coverage'
 *                        issue the legacy pct/collision keys already use —
 *                        same "go look at this list" shape, not a new
 *                        visual category.
 *   observability      → InfoItem (tone 'quality'), NEVER an Issue, NEVER
 *                        touches `sev` — a real nonzero count that falls
 *                        outside every counted lane (e.g. reorder-parity
 *                        before an owner has recorded a baseline) stays
 *                        individually visible instead of vanishing; see
 *                        build_warnings_v2's docstring for why this kind
 *                        exists at all.
 *
 * v2OwnedKeys excludes from the legacy bucket any coverage_warnings key a
 * warnings_v2 entry now renders BETTER, so nothing double-renders: the 3
 * completeness pct keys, and the unsplit orders_daily_exact_duplicate_rows
 * key once the new split (legacy/new) entries exist for it.
 */
function deriveOpsHealth(s: OpsSources): Subsystem {
  const d = s.opsDeep
  const issues: Issue[] = []
  const info: InfoItem[] = []
  let sev: Sev = 'unknown'
  let summary = 'unknown'
  if (d.data) {
    const h = d.data
    sev = 'green'
    // BQ unreachable = the whole sync pipeline is blind → impaired.
    if (h.bq_reachable === false) {
      sev = worstSev([sev, 'orange'])
      issues.push({
        key: 'opshealth-bq', sev: 'orange', subsystem: 'Ops Health',
        title: 'BigQuery unreachable',
        detail: 'bq_reachable=false — every BQ-sourced sync (orders, products, PPC, inventory) is blind.',
        hint: 'Check network/credentials on the mini; syncs will heal on the next tick once BQ answers.',
      })
    }
    // Audit chain: verified-failed is a tamper/corruption signal (orange);
    // never-verified means the nightly verify script has not recorded a
    // result yet (yellow — never silent-green).
    if (h.audit_chain_valid === false) {
      const never = h.audit_chain_last_verified_at === 'not_yet_verified'
      const aSev: Issue['sev'] = never ? 'yellow' : 'orange'
      sev = worstSev([sev, aSev])
      issues.push({
        key: 'opshealth-audit', sev: aSev, subsystem: 'Ops Health',
        title: never ? 'Audit chain never verified' : 'Audit chain verification FAILED',
        detail: never
          ? 'No _audit_chain_verify row in md_sync_state — scripts/verify_audit_chain.py has not recorded a nightly run yet.'
          : `Last verify at ${h.audit_chain_last_verified_at ?? '?'} reported the hash chain broken.`,
        hint: never
          ? 'Run scripts/verify_audit_chain.py (or schedule it) so tamper-evidence is actually checked.'
          : 'Treat the audit log as suspect — investigate before trusting who-did-what.',
      })
    }
    const v2 = h.warnings_v2
    const v2OwnedKeys = new Set<string>()
    const v2ExtraCoverageLines: string[] = []
    if (v2) {
      const hasDupSplit = v2.some(
        (w) => w.key === 'orders_daily_dup_rows_legacy' || w.key === 'orders_daily_dup_rows_new')
      for (const w of v2) {
        if (w.kind === 'completeness') v2OwnedKeys.add(w.key)
        if (w.kind === 'work_queue' || w.kind === 'needs_calibration') {
          v2ExtraCoverageLines.push(
            `${w.label}${w.value != null ? `: ${w.value}${w.unit ?? ''}` : ''}`)
        }
      }
      if (hasDupSplit) v2OwnedKeys.add('orders_daily_exact_duplicate_rows')
    }
    // Coverage warnings — the backend computes these (<85% coverage, or >50
    // stale-ETA POs). Data-quality signals: yellow, with the real numbers.
    const warns = (h.coverage_warnings ?? []).filter((k) => !v2OwnedKeys.has(k))
    if (warns.length || v2ExtraCoverageLines.length) {
      sev = worstSev([sev, 'yellow'])
      const fmt = (k: string): string => {
        const v = h.coverage[k]
        const label = COVERAGE_LABEL[k] ?? k
        if (typeof v === 'number') {
          return k.endsWith('_pct') ? `${(v * 100).toFixed(1)}% ${label}` : `${v} ${label}`
        }
        return label
      }
      const detailParts = [...warns.map(fmt), ...v2ExtraCoverageLines]
      issues.push({
        key: 'opshealth-coverage', sev: 'yellow', subsystem: 'Ops Health',
        title: `${detailParts.length} coverage warning${detailParts.length > 1 ? 's' : ''}`,
        detail: detailParts.join(' · '),
        hint: 'Data-quality gaps (not an outage): fill costs/suppliers, close abandoned POs.',
      })
    }
    if (v2) {
      const infraW = v2.find((w) => w.kind === 'infra')
      if (infraW) {
        const infraSev: Exclude<Sev, 'green' | 'unknown'> =
          infraW.key === 'disk_free_gb_critical' ? 'orange' : 'yellow'
        sev = worstSev([sev, infraSev])
        issues.push({
          key: 'opshealth-infra', sev: infraSev, subsystem: 'Ops Health',
          title: `${infraW.label}: ${infraW.value ?? '?'}${infraW.unit ? ` ${infraW.unit}` : ''}`,
          detail: infraW.action_label ?? 'Free disk FIRST, then restart.',
        })
      }
      const tripwireW = v2.find((w) => w.kind === 'tripwire')
      if (tripwireW) {
        sev = worstSev([sev, 'orange'])
        issues.push({
          key: 'opshealth-tripwire', sev: 'orange', subsystem: 'Ops Health',
          title: tripwireW.label,
          detail: `${tripwireW.value ?? ''}${tripwireW.unit ? ` ${tripwireW.unit}` : ''}`.trim(),
        })
      }
      for (const w of v2) {
        if (w.kind === 'completeness') {
          info.push({
            key: `opshealth-completeness-${w.key}`, subsystem: 'Ops Health', tone: 'quality',
            title: w.label,
            detail: w.value == null
              ? 'unknown — no data recorded yet'
              : `${(Number(w.value) * 100).toFixed(1)}%${w.since ? ' — dropped more than 2pts vs a week ago' : ''}`,
          })
        } else if (w.kind === 'awaiting_decision') {
          info.push({
            key: `opshealth-decision-${w.key}`, subsystem: 'Ops Health', tone: 'decision',
            title: w.label, detail: w.action_label ?? undefined,
          })
        } else if (w.kind === 'observability') {
          // Never counted, never touches `sev` — see build_warnings_v2's
          // `observability` kind docstring: this is what keeps a real,
          // nonzero defect count individually visible while it sits outside
          // every counted lane (e.g. reorder-parity before an owner has
          // recorded a baseline).
          // meta.actionUrl: where the line can send someone (PR 20b — the
          // vessel-tracking line links to the Shipments map). Additive.
          info.push({
            key: `opshealth-observability-${w.key}`, subsystem: 'Ops Health', tone: 'quality',
            title: `${w.label}${w.value != null ? `: ${w.value}${w.unit ?? ''}` : ''}`,
            detail: w.action_label ?? undefined,
            meta: { actionUrl: w.action_url ?? null },
          })
        }
      }
    }
    // Ops job tick: the fastest ops job is the 15-min BQ sync, so >30 min
    // without ANY ops job firing means the ops half of the scheduler is stuck.
    const tick = h.scheduler_last_tick_age_s
    if (tick != null && tick > 1800) {
      sev = worstSev([sev, 'orange'])
      issues.push({
        key: 'opshealth-tick', sev: 'orange', subsystem: 'Ops Health',
        title: 'Ops sync jobs not firing',
        detail: `No ops job has run in ${(tick / 60).toFixed(0)} min (fastest cadence is 15 min).`,
      })
    }
    const parts = [`status ${h.status}`]
    if (warns.length) parts.push(`${warns.length} coverage warn`)
    if (h.audit_chain_valid === false) parts.push('audit chain ✗')
    if (h.bq_reachable != null) parts.push(`BQ ${h.bq_reachable ? 'ok' : 'DOWN'}`)
    summary = parts.join(' · ')
  }
  return { id: 'ops-health', name: 'Ops Health (inventory half)', sev, summary, issues, info }
}

/**
 * Observability InfoItems that describe a LIVE FEED rather than a data
 * defect — they render as their own always-visible line under System
 * health's "Data feeds" label (components/health/LiveFeedLines.tsx), not
 * inside the collapsed Ops Health card, where the vessel-tracking line sat
 * unseen after PR 20 (prod check 10-13). Still InfoItems: never counted,
 * never touch `sev`.
 */
export const LIVE_FEED_INFO_KEYS: readonly string[] = ['opshealth-observability-ais_tracking']

export function isLiveFeedItem(i: InfoItem): boolean {
  return LIVE_FEED_INFO_KEYS.includes(i.key)
}

/** The live-feed lines among `info`, in the order they came. */
export function liveFeedItems(info: readonly InfoItem[]): InfoItem[] {
  return info.filter(isLiveFeedItem)
}

/** Same rollup scripts/dev/prod-status.ps1 prints: a source is ERROR when
 *  last_status != ok, STALE when last_run_at is older than 26h (nightly
 *  cadence + slack). Covers BOTH halves via /api/admin/freshness-unified:
 *  md_sync_state (ops jobs) and bbpro_sync_state (BQ table mirrors). */
const SYNC_STALE_HOURS = 26

// UI overhaul PR-3b (audit SET-02): measured on the SERVER's clock, a bare
// date is a local calendar date, and an age is never negative — "-479 min"
// was a date-only value parsed as UTC midnight against this laptop's clock.
function ageHours(iso: string | null): number | null {
  return ageHoursSince(iso)
}

export type SyncRollupRow = {
  half: 'ops' | 'adbetter'
  name: string
  status: string | null
  age_h: number | null
  error: string | null
  stale: boolean
  failed: boolean
  degraded: boolean
  /** migration ops/093 / 148. Undefined on an old-shaped row. */
  skip_reason?: string | null
  /** backend/ops/sync/sources.py:ACTIVE_SOURCE_KEYS — ops rows only
   *  (undefined on the adbetter half, which has no such registry yet). */
  active?: boolean
  /** Set when this row renders as an InfoItem instead of counting toward
   *  failed/degraded/stale — skip_reason-driven or active:false. */
  info?: { tone: InfoItem['tone']; title: string; detail?: string }
}

/** Ledger statuses that are NOT 'ok' but are not a broken pipeline either.
 *
 *  Previously anything !== 'ok' was rendered as an orange "sync source
 *  failing". That made a benign empty seed on a fresh DB ('no_data') look
 *  identical to a genuinely broken job ('failed'), which is what
 *  backend/sync/junglescout.py's own comment claims stays "distinguishable
 *  end-to-end" — true of the Alerts subsystem's severity column, but not
 *  here, and this panel is what an operator actually watches. */
const BENIGN_SYNC_STATUSES = new Set(['no_data', 'degraded', 'skipped'])

type SyncClassification = {
  failed: boolean
  degraded: boolean
  info?: { tone: InfoItem['tone']; title: string; detail?: string }
}

/**
 * D-signal-taxonomy: prefer skip_reason over the bare status string once a
 * backend supplies one — it says WHY a 'skipped' row is skipped, so an
 * operator stops seeing three visually-identical "degraded" chips for three
 * unrelated situations (the other half owns this feed / the upstream table
 * doesn't exist yet / a required precondition failed).
 *
 *   other_half_owns            → InfoItem 'historical': covered elsewhere.
 *   upstream_missing_optional  → InfoItem 'waiting': nothing to do yet.
 *   switched_off               → InfoItem 'guardrail': the owner turned it off.
 *   upstream_missing_required,
 *   precondition_missing       → still a real failing issue.
 *
 * `active: false` (a source key no currently-running sync module writes —
 * a retired/renamed code path) is checked FIRST and always wins: such a
 * row is never counted as failed/degraded regardless of its last recorded
 * status, and renders dimmed as historical.
 *
 * No skip_reason at all (an old backend) falls through to exactly today's
 * mapping — never a false green on a mixed-version deploy.
 */
function classifySync(status: string | null, skipReason?: string | null,
                      active?: boolean): SyncClassification {
  if (active === false) {
    return { failed: false, degraded: false,
            info: { tone: 'historical', title: 'No longer produced — historical' } }
  }
  switch (skipReason) {
    case 'other_half_owns':
      return { failed: false, degraded: false,
              info: { tone: 'historical', title: 'Covered by the AdBetter half' } }
    case 'upstream_missing_optional':
      return { failed: false, degraded: false,
              info: { tone: 'waiting', title: 'Upstream feed not live yet' } }
    case 'switched_off':
      // PR 19: the owner turned the job off (e.g. CLICKPAY_LINK_MATCHER=0).
      // Not failing, and not stale either: it is not supposed to run.
      return { failed: false, degraded: false,
              info: { tone: 'guardrail', title: 'Switched off on purpose' } }
    case 'upstream_missing_required':
    case 'precondition_missing':
      return { failed: true, degraded: false }
    default:
      break
  }
  const s = (status ?? '').trim()
  if (s === 'ok' || s === '') return { failed: false, degraded: false }
  if (BENIGN_SYNC_STATUSES.has(s)) return { failed: false, degraded: true }
  return { failed: true, degraded: false }
}

export function syncRollup(u: FreshnessUnifiedOut): SyncRollupRow[] {
  const rows: SyncRollupRow[] = []
  for (const r of u.ops.sync_state) {
    const age = ageHours(r.last_run_at)
    const cls = classifySync(r.last_status, r.skip_reason, r.active)
    rows.push({
      half: 'ops', name: r.source, status: r.last_status, age_h: age,
      error: r.last_error, failed: cls.failed, degraded: cls.degraded,
      stale: age != null && age > SYNC_STALE_HOURS && !cls.info,
      skip_reason: r.skip_reason, active: r.active, info: cls.info,
    })
  }
  for (const r of u.adbetter.sync_state) {
    const age = ageHours(r.last_sync_at)
    // PR 22c (B21): `active` (backend/sync/sync_state_sources.py) marks a mirror
    // row no running sync writes any more (temu_shipment_costs): an info line,
    // never stale. Absent (old backend) = unknown, still graded as before.
    const cls = classifySync(r.sync_status, r.skip_reason, r.active)
    if (cls.info && r.active === false && r.last_sync_at) {
      cls.info = { ...cls.info, detail: `last synced ${fmtMonthDay(r.last_sync_at.slice(0, 10))}` }
    }
    rows.push({
      half: 'adbetter', name: r.local_table, status: r.sync_status, age_h: age,
      error: r.note, failed: cls.failed, degraded: cls.degraded,
      stale: age != null && age > SYNC_STALE_HOURS && !cls.info,
      skip_reason: r.skip_reason, active: r.active, info: cls.info,
    })
  }
  return rows
}

function deriveSyncState(s: OpsSources): Subsystem {
  const u = s.unified
  const issues: Issue[] = []
  const info: InfoItem[] = []
  let sev: Sev = 'unknown'
  let summary = 'unknown'
  if (u.data) {
    const rows = syncRollup(u.data)
    const failed = rows.filter((r) => r.failed)
    const degraded = rows.filter((r) => r.degraded)
    const stale = rows.filter((r) => !r.failed && !r.degraded && r.stale)
    for (const r of rows) {
      if (r.info) {
        info.push({
          key: `sync-info-${r.half}-${r.name}`, subsystem: 'Sync State',
          tone: r.info.tone, title: `${r.name}: ${r.info.title}`,
          detail: [r.info.detail, r.status ? `last recorded status: ${r.status}` : null]
            .filter(Boolean).join(' · ') || undefined,
        })
      }
    }
    const sectionErrs = [
      u.data.ops.sync_state_error, u.data.adbetter.sync_state_error,
    ].filter(Boolean) as string[]
    sev = failed.length ? 'orange'
      : (degraded.length || stale.length || sectionErrs.length) ? 'yellow' : 'green'
    summary = `${rows.length} sources · ${failed.length} error · `
      + `${degraded.length} degraded · ${stale.length} stale`
    if (failed.length) {
      // "last failed N d ago" — the age a failing row's OWN last_run_at
      // carries, so a chronic failure reads as chronic, not as "just
      // happened" on every page load.
      const failedDetail = (r: SyncRollupRow): string => {
        const base = `${r.name}: ${(r.error ?? r.status ?? 'failed').slice(0, 60)}`
        if (r.age_h == null) return base
        const days = r.age_h / 24
        return `${base} (last failed ${days >= 1 ? `${days.toFixed(0)}d` : `${r.age_h.toFixed(0)}h`} ago)`
      }
      issues.push({
        key: 'sync-error', sev: 'orange', subsystem: 'Sync State',
        title: `${failed.length} sync source${failed.length > 1 ? 's' : ''} failing`,
        detail: failed.slice(0, 4).map(failedDetail).join(' · '),
        hint: 'Fix the failing source, then re-run it (the Fix button retries mappable ops jobs).',
      })
    }
    if (degraded.length) {
      issues.push({
        key: 'sync-degraded', sev: 'yellow', subsystem: 'Sync State',
        title: `${degraded.length} sync source${degraded.length > 1 ? 's' : ''} degraded`,
        detail: degraded.slice(0, 4).map((r) =>
          `${r.name}: ${(r.error ?? r.status ?? 'degraded').slice(0, 60)}`).join(' · '),
        hint: 'Ran, but produced partial or no data. Not broken — check the source has anything to give.',
      })
    }
    if (stale.length) {
      const worst = [...stale].sort((a, b) => (b.age_h ?? 0) - (a.age_h ?? 0))[0]
      issues.push({
        key: 'sync-stale', sev: 'yellow', subsystem: 'Sync State',
        title: `${stale.length} sync source${stale.length > 1 ? 's' : ''} stale (>${SYNC_STALE_HOURS}h)`,
        detail: `Oldest: ${worst.name} ${worst.age_h != null ? `${worst.age_h.toFixed(0)}h` : '?'} ago`,
        hint: 'The owning job has not completed in over a day — check the scheduler / job registry.',
      })
    }
    for (const e of sectionErrs) {
      issues.push({
        key: `sync-section-${e.slice(0, 12)}`, sev: 'yellow', subsystem: 'Sync State',
        title: 'A sync-state section could not be read',
        detail: e.slice(0, 160),
      })
    }
  }
  return { id: 'sync-state', name: 'Sync State (both halves)', sev, summary, issues, info }
}

function deriveRegistry(s: OpsSources): Subsystem {
  const j = s.syncJobs
  const issues: Issue[] = []
  let sev: Sev = 'unknown'
  let summary = 'unknown'
  if (j.data) {
    const d = j.data
    if (!d.scheduler_running) {
      // With the scheduler down every job reads registered=false, so the
      // registry view is meaningless — the Scheduler subsystem owns that
      // outage; here we only refuse to claim anything.
      sev = 'unknown'
      summary = 'scheduler down — registration unknown'
    } else {
      const unknown = d.live_jobs_not_in_registry
      const offJobs = d.jobs.filter((x) => !x.registered)
      sev = 'green'
      if (unknown.length) {
        // The cutover runbook asserts this list is EMPTY: a live job outside
        // the ownership map is mutating data nobody accounts for.
        sev = 'red'
        issues.push({
          key: 'registry-unknown-jobs', sev: 'red', subsystem: 'Job Registry',
          title: `${unknown.length} live job${unknown.length > 1 ? 's' : ''} NOT in the ownership registry`,
          detail: unknown.slice(0, 6).join(' · '),
          hint: 'Every scheduled job must have a backend/sync_registry.py entry — find who registered this and add it (or kill it).',
        })
      }
      if (d.db_mode !== 'unified') {
        sev = worstSev([sev, 'yellow'])
        issues.push({
          key: 'registry-db-split', sev: 'yellow', subsystem: 'Job Registry',
          title: 'DB mode is split (two DuckDB files)',
          detail: 'CLICKPAY_DB_PATH is not driving both halves — expected "unified" post-cutover.',
          hint: 'Fine on a dev sandbox; on production this is a misconfiguration.',
        })
      }
      summary = `${d.db_mode} · ${d.jobs.length - offJobs.length}/${d.jobs.length} registered live · ${d.live_jobs.length} running`
    }
  }
  return { id: 'registry', name: 'Job Registry (Stage 4)', sev, summary, issues, info: [] }
}

/** Build the full subsystem list (order = display order). */
export function deriveSubsystems(s: OpsSources): Subsystem[] {
  return [
    deriveDatabase(s),
    deriveMigrations(s),
    deriveScheduler(s),
    deriveRegistry(s),
    deriveFreshness(s),
    deriveSyncState(s),
    deriveOpsHealth(s),
    deriveCircuit(s),
    deriveAlerts(s),
  ]
}

/**
 * Global rollup. GREEN is never inferred from a partial read — 'unknown'
 * (rank 1, above green) floats up worst-of, so a subsystem we couldn't read
 * shows the dot as gray "checking…", never a false green. And even an
 * all-green read requires the two hard-fail subsystems (db/migrations) to be
 * affirmatively green.
 */
export function rollup(subs: Subsystem[]): Sev {
  const sevs = subs.map((x) => x.sev)
  const w = worstSev(sevs)
  if (w === 'green') {
    const db = subs.find((x) => x.id === 'database')?.sev
    const mig = subs.find((x) => x.id === 'migrations')?.sev
    if (db !== 'green' || mig !== 'green') return 'unknown'
  }
  return w
}

export const SEV_DOT: Record<Sev, string> = {
  green: 'bg-green-500',
  yellow: 'bg-amber-400',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  unknown: 'bg-gray-300',
}

export const SEV_TEXT: Record<Sev, string> = {
  green: 'text-green-700',
  yellow: 'text-amber-700',
  orange: 'text-orange-700',
  red: 'text-red-700',
  unknown: 'text-gray-500',
}

export const SEV_LABEL: Record<Sev, string> = {
  green: 'all systems go',
  yellow: 'minor issues',
  orange: 'automation impaired',
  red: 'system down',
  unknown: 'checking…',
}

// ── Shared store ────────────────────────────────────────────────────────────
//
// ONE poller feeds the hub page AND the header dot. Whoever subscribes first
// starts it; the hub's manual "re-check" calls refreshOpsStatus() and every
// subscriber re-renders off the same snapshot — by construction the dot and
// the hub can never disagree.

export type OpsSnapshot = {
  s: OpsSources | null
  updatedAt: number
  loading: boolean
}

const POLL_MS = 30_000

let snapshot: OpsSnapshot = { s: null, updatedAt: 0, loading: false }
const listeners = new Set<() => void>()
let timer: number | null = null
let paused = false
let inflight: Promise<void> | null = null

// Generation stamp: every call that starts a new fetch (refreshOpsStatus OR
// refetchFresh) increments this and captures its own value. A fetch's
// .then()/.catch()/.finally() only touches `snapshot`/`inflight` if its
// captured generation still matches the module's current one — otherwise a
// newer fetch has since started and this one's result is stale and must be
// dropped, not committed. Without this, refetchFresh's drain-by-timeout race
// (below) only stops AWAITING an old poll after the timeout; it does not
// cancel it, so a merely-slow (not hung) prior poll can still resolve later
// and silently overwrite the store with pre-fix data after refetchFresh has
// already committed and returned the correct fresh snapshot.
let generation = 0

// Build 52a: this is a MODULE-level singleton poller shared by every
// subscriber (header dot, notifications bell, CommandCenter, SystemHealth) —
// there is exactly one timer for the whole app, so it's gated here once
// rather than per-component. A backgrounded browser tab is the #1 idle-load
// source measured in prod (Build 47 perf data, 9h idle window: ~1,029 hits
// on every fan-out member with nobody at the keyboard) — pause the interval
// while `document.hidden`, and do one immediate refresh + resume the moment
// the tab comes back, so the status never goes stale silently.
let tabHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'

function emit(): void {
  listeners.forEach((fn) => fn())
}

export function getOpsSnapshot(): OpsSnapshot {
  return snapshot
}

/**
 * Start one fetch (plain poll or fresh=1 verify) as the new current
 * generation and wire its settlement to only touch shared state (`snapshot`,
 * `inflight`) while it remains the current generation. Assigns the resulting
 * promise to `inflight` itself so callers can read/await it the same way
 * regardless of which entry point started it.
 */
function startFetch(fresh: boolean): Promise<void> {
  const myGen = ++generation
  snapshot = { ...snapshot, loading: true }
  emit()
  inflight = fetchOpsSources(fresh)
    .then((s) => {
      if (myGen !== generation) return // superseded — drop this stale result
      snapshot = { s, updatedAt: Date.now(), loading: false }
    })
    .catch(() => {
      if (myGen !== generation) return
      snapshot = { ...snapshot, loading: false }
    }) // settle() makes this unreachable; belt+braces
    .finally(() => {
      if (myGen === generation) inflight = null
      emit()
    })
  return inflight
}

export function refreshOpsStatus(): Promise<void> {
  if (inflight) return inflight
  return startFetch(false)
}

/** Test hook only: lets refetchFresh's inflight-drain race resolve in
 *  milliseconds instead of the real 10s. Production never calls this. */
let REFETCH_DRAIN_TIMEOUT_MS = 10_000
export function __setRefetchDrainTimeoutMsForTest(ms: number): void {
  REFETCH_DRAIN_TIMEOUT_MS = ms
}

/**
 * Force a fresh read of every source — bypassing the server's 10s rollup
 * cache — and commit it to the shared store. This is what a post-fix verify
 * must call, never refreshOpsStatus(): a plain poll can (a) reuse an
 * `inflight` request that started BEFORE the caller's fix ran (the reuse
 * trap above — `if (inflight) return inflight`) and (b) land inside the
 * backend's short single-flight cache window, so either alone hands the
 * caller pre-fix data and lets "fixed" be claimed off a stale read — the
 * seam behind the owner's "the fix button doesn't end up resolving the fix"
 * complaint.
 */
export async function refetchFresh(): Promise<OpsSources | null> {
  if (inflight) {
    // Drain whatever poll was already in flight before racing our own fetch
    // — its result reflects pre-mutation state and must not be mistaken for
    // the fresh read. Timeout-raced so a hung poll can never wedge a verify.
    await Promise.race([
      inflight,
      new Promise<void>((resolve) => { setTimeout(resolve, REFETCH_DRAIN_TIMEOUT_MS) }),
    ])
  }
  // The drained inflight promise above may have been a merely-slow (not
  // hung) poll that is still going to settle later on its own — startFetch's
  // generation bump below is what stops that late settlement from clobbering
  // the fresh snapshot we're about to commit, not the drain itself.
  await startFetch(true)
  return getOpsSnapshot().s
}

function ensurePolling(): void {
  if (timer != null || paused || tabHidden || listeners.size === 0) return
  timer = window.setInterval(() => { refreshOpsStatus() }, POLL_MS)
}

function stopPolling(): void {
  if (timer != null) { window.clearInterval(timer); timer = null }
}

export function setOpsPaused(p: boolean): void {
  paused = p
  if (p) stopPolling()
  else ensurePolling()
  emit()
}

export function isOpsPaused(): boolean {
  return paused
}

/** Exposed for the visibility-census e2e spec; not for app code. */
export function isOpsTabHidden(): boolean {
  return tabHidden
}

function onVisibilityChange(): void {
  const nowHidden = document.visibilityState === 'hidden'
  if (nowHidden === tabHidden) return
  tabHidden = nowHidden
  if (tabHidden) {
    stopPolling()
  } else if (listeners.size > 0 && !paused) {
    // Immediate refresh on return so the dot never shows a stale snapshot
    // as if it were live, then resume the normal cadence.
    refreshOpsStatus()
    ensurePolling()
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', onVisibilityChange)
}

export function subscribeOpsStatus(fn: () => void): () => void {
  listeners.add(fn)
  ensurePolling()
  if (snapshot.updatedAt === 0 && !inflight) refreshOpsStatus()
  return () => {
    listeners.delete(fn)
    if (listeners.size === 0) stopPolling()
  }
}

/** Subscribe a component to the shared status snapshot. */
export function useOpsStatus(): OpsSnapshot {
  return useSyncExternalStore(subscribeOpsStatus, getOpsSnapshot)
}
